import { Component, Input, computed, signal, OnInit, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PublicI18nService } from '@frontend/public-calendar';
import {
  FooterSettings,
  FooterContent,
  FooterLayoutVariant,
  SocialLink,
  LegalPageConfig,
  NavLink,
  BackgroundType,
  SECTION_THEME,
} from './section-renderer.models';

/**
 * Event emitted when a legal page link is clicked in preview mode.
 */
export interface LegalPageNavigationEvent {
  pageType: 'imprint' | 'privacy';
}

/** Internal nav link with resolved label and URL */
interface ResolvedNavLink {
  label: string;
  url: string;
}

/**
 * Footer Section Renderer
 * Full-width footer sections with multiple layout variants.
 * Based on Tailwind Plus footer examples.
 */
@Component({
  selector: 'dcp-footer-renderer',
  standalone: true,
  imports: [CommonModule],
  template: `
    <footer [style.background]="backgroundStyle()">
      @switch (layoutVariant()) {
        @case ('simple-centered') {
          <!-- Simple Centered Footer -->
          <div class="mx-auto max-w-7xl overflow-hidden px-6 py-20 sm:py-24 lg:px-8">
            <!-- Company Name and Tagline -->
            @if (companyName() || tagline()) {
              <div class="text-center mb-8">
                @if (companyName()) {
                  <p class="text-lg font-semibold" [style.color]="theme.textColor">{{ companyName() }}</p>
                }
                @if (tagline()) {
                  <p class="text-sm mt-1" [style.color]="theme.textMutedColor">{{ tagline() }}</p>
                }
              </div>
            }

            <!-- Navigation Links (includes legal links in order) -->
            @if (settings().showNavLinks !== false && allNavLinks().length) {
              <nav aria-label="Footer" class="-mb-6 flex flex-wrap justify-center gap-x-12 gap-y-3 text-sm/6">
                @for (link of allNavLinks(); track link.label) {
                  <a
                    [href]="normalizeUrl(link.url)"
                    [title]="isLegalUrl(link.url) ? 'Opens: ' + link.url : ''"
                    class="transition-colors hover:underline"
                    [class.cursor-pointer]="isLegalUrl(link.url)"
                    [class.cursor-default]="previewMode && !isLegalUrl(link.url)"
                    [style.color]="theme.textMutedColor"
                    (click)="onLinkClick($event, link)"
                  >
                    {{ link.label }}
                  </a>
                }
              </nav>
            }

            <!-- Social Links -->
            @if (showSocialLinks() && socialLinks().length) {
              <div class="mt-16 flex justify-center gap-x-10">
                @for (social of socialLinks(); track social.platform) {
                  <a
                    [href]="social.url"
                    class="transition-colors"
                    [style.color]="theme.textMutedColor"
                  >
                    <span class="sr-only">{{ social.platform }}</span>
                    <ng-container [ngSwitch]="social.platform">
                      <svg *ngSwitchCase="'facebook'" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" class="size-6">
                        <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clip-rule="evenodd" fill-rule="evenodd" />
                      </svg>
                      <svg *ngSwitchCase="'instagram'" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" class="size-6">
                        <path d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clip-rule="evenodd" fill-rule="evenodd" />
                      </svg>
                      <svg *ngSwitchCase="'twitter'" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" class="size-6">
                        <path d="M13.6823 10.6218L20.2391 3H18.6854L12.9921 9.61788L8.44486 3H3.2002L10.0765 13.0074L3.2002 21H4.75404L10.7663 14.0113L15.5685 21H20.8131L13.6819 10.6218H13.6823ZM11.5541 13.0956L10.8574 12.0991L5.31391 4.16971H7.70053L12.1742 10.5689L12.8709 11.5655L18.6861 19.8835H16.2995L11.5541 13.096V13.0956Z" />
                      </svg>
                      <svg *ngSwitchCase="'linkedin'" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" class="size-6">
                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                      </svg>
                      <svg *ngSwitchCase="'youtube'" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" class="size-6">
                        <path d="M19.812 5.418c.861.23 1.538.907 1.768 1.768C21.998 8.746 22 12 22 12s0 3.255-.418 4.814a2.504 2.504 0 0 1-1.768 1.768c-1.56.419-7.814.419-7.814.419s-6.255 0-7.814-.419a2.505 2.505 0 0 1-1.768-1.768C2 15.255 2 12 2 12s0-3.255.417-4.814a2.507 2.507 0 0 1 1.768-1.768C5.744 5 11.998 5 11.998 5s6.255 0 7.814.418ZM15.194 12 10 15V9l5.194 3Z" clip-rule="evenodd" fill-rule="evenodd" />
                      </svg>
                      <svg *ngSwitchCase="'github'" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" class="size-6">
                        <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clip-rule="evenodd" fill-rule="evenodd" />
                      </svg>
                    </ng-container>
                  </a>
                }
              </div>
            }

            <!-- Copyright -->
            @if (settings().showCopyright !== false) {
              <p class="mt-10 text-center text-sm/6" [style.color]="theme.textMutedColor">
                {{ copyrightText() }}
              </p>
            }
          </div>
        }

        @case ('with-social') {
          <!-- Footer with prominent social links -->
          <div class="mx-auto max-w-7xl px-6 py-12 lg:px-8">
            <!-- Company Name and Tagline -->
            @if (companyName() || tagline()) {
              <div class="text-center mb-6">
                @if (companyName()) {
                  <p class="text-lg font-semibold" [style.color]="theme.textColor">{{ companyName() }}</p>
                }
                @if (tagline()) {
                  <p class="text-sm mt-1" [style.color]="theme.textMutedColor">{{ tagline() }}</p>
                }
              </div>
            }

            <div class="flex flex-col items-center gap-6"
                 [class.sm:flex-row]="showSocialLinks() && socialLinks().length"
                 [class.sm:justify-between]="showSocialLinks() && socialLinks().length"
                 [class.justify-center]="!showSocialLinks() || !socialLinks().length">
              <!-- Navigation Links (includes legal links in order) -->
              @if (settings().showNavLinks !== false && allNavLinks().length) {
                <nav class="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm/6">
                  @for (link of allNavLinks(); track link.label) {
                    <a
                      [href]="normalizeUrl(link.url)"
                      [title]="isLegalUrl(link.url) ? 'Opens: ' + link.url : ''"
                      class="transition-colors hover:underline"
                      [class.cursor-pointer]="isLegalUrl(link.url)"
                      [class.cursor-default]="previewMode && !isLegalUrl(link.url)"
                      [style.color]="theme.textMutedColor"
                      (click)="onLinkClick($event, link)"
                    >
                      {{ link.label }}
                    </a>
                  }
                </nav>
              }

              <!-- Social Links -->
              @if (showSocialLinks() && socialLinks().length) {
                <div class="flex gap-x-6">
                  @for (social of socialLinks(); track social.platform) {
                    <a
                      [href]="social.url"
                      class="transition-colors"
                      [style.color]="theme.textMutedColor"
                    >
                      <span class="sr-only">{{ social.platform }}</span>
                      <ng-container [ngSwitch]="social.platform">
                        <svg *ngSwitchCase="'facebook'" viewBox="0 0 24 24" fill="currentColor" class="size-5">
                          <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/>
                        </svg>
                        <svg *ngSwitchCase="'instagram'" viewBox="0 0 24 24" fill="currentColor" class="size-5">
                          <path d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"/>
                        </svg>
                        <svg *ngSwitchCase="'twitter'" viewBox="0 0 24 24" fill="currentColor" class="size-5">
                          <path d="M13.6823 10.6218L20.2391 3H18.6854L12.9921 9.61788L8.44486 3H3.2002L10.0765 13.0074L3.2002 21H4.75404L10.7663 14.0113L15.5685 21H20.8131L13.6819 10.6218H13.6823ZM11.5541 13.0956L10.8574 12.0991L5.31391 4.16971H7.70053L12.1742 10.5689L12.8709 11.5655L18.6861 19.8835H16.2995L11.5541 13.096V13.0956Z"/>
                        </svg>
                        <svg *ngSwitchDefault viewBox="0 0 24 24" fill="currentColor" class="size-5">
                          <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"/>
                        </svg>
                      </ng-container>
                    </a>
                  }
                </div>
              }
            </div>

            <!-- Copyright -->
            @if (settings().showCopyright !== false) {
              <p class="mt-8 text-center text-sm/6" [style.color]="theme.textMutedColor">
                {{ copyrightText() }}
              </p>
            }
          </div>
        }

        @default {
          <!-- Minimal Footer -->
          <div class="mx-auto max-w-7xl px-6 py-8 lg:px-8">
            <!-- Company Name and Tagline -->
            @if (companyName() || tagline()) {
              <div class="text-center mb-4">
                @if (companyName()) {
                  <p class="text-base font-semibold" [style.color]="theme.textColor">{{ companyName() }}</p>
                }
                @if (tagline()) {
                  <p class="text-xs mt-1" [style.color]="theme.textMutedColor">{{ tagline() }}</p>
                }
              </div>
            }

            <p class="text-center text-sm/6" [style.color]="theme.textMutedColor">
              {{ copyrightText() }}
            </p>

            <!-- Navigation Links (includes legal links in order) -->
            @if (allNavLinks().length > 0) {
              <nav class="mt-4 flex justify-center gap-x-6 text-sm/6">
                @for (link of allNavLinks(); track link.label) {
                  <a
                    [href]="normalizeUrl(link.url)"
                    [title]="isLegalUrl(link.url) ? 'Opens: ' + link.url : ''"
                    class="transition-colors hover:underline"
                    [class.cursor-pointer]="isLegalUrl(link.url)"
                    [class.cursor-default]="previewMode && !isLegalUrl(link.url)"
                    [style.color]="theme.textMutedColor"
                    (click)="onLinkClick($event, link)"
                  >
                    {{ link.label }}
                  </a>
                }
              </nav>
            }
          </div>
        }
      }
    </footer>

    <!-- Toast notification for legal link feedback -->
    @if (toastMessage()) {
      <div class="fixed top-4 left-1/2 -translate-x-1/2 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 text-sm font-medium animate-fade-in">
        {{ toastMessage() }}
      </div>
    }
  `,
})
export class FooterRendererComponent implements OnInit {
  /** Section settings */
  @Input() set settingsInput(value: Record<string, unknown>) {
    this._settings.set(this.normalizeSettings(value));
  }
  private _settings = signal<FooterSettings>({});
  readonly settings = this._settings.asReadonly();

  /** Section content */
  @Input() set contentInput(value: Record<string, unknown>) {
    this._content.set(this.normalizeContent(value));
  }
  private _content = signal<FooterContent>({});
  readonly content = this._content.asReadonly();

  /** Theme injected from parent fullwidth-preview */
  private readonly themeSignal = inject(SECTION_THEME);
  protected get theme() { return this.themeSignal(); }

  /** I18n service for reactive locale-based content resolution */
  private readonly i18n = inject(PublicI18nService);

  /** Legal page configuration for auto-links (signal-based for reactivity) */
  private readonly _legalPageConfig = signal<LegalPageConfig | undefined>(undefined);
  @Input() set legalPageConfig(value: LegalPageConfig | undefined) {
    this._legalPageConfig.set(value);
  }
  get legalPageConfig(): LegalPageConfig | undefined {
    return this._legalPageConfig();
  }

  /** Current hostname for URL normalization (e.g., 'mycompany.availme.net') */
  @Input() currentHostname?: string;

  /** Preview mode - disables link navigation */
  @Input() previewMode = false;

  /** Current locale for label resolution (signal-based for reactivity) */
  private readonly _locale = signal<'de' | 'en'>('de');
  @Input() set locale(value: 'de' | 'en') {
    this._locale.set(value);
  }
  get locale(): 'de' | 'en' {
    return this._locale();
  }

  /** Emitted when a legal page link is clicked in preview mode */
  @Output() legalPageNavigation = new EventEmitter<LegalPageNavigationEvent>();

  /** Current year for copyright */
  readonly currentYear = new Date().getFullYear();

  ngOnInit(): void {
    // Component initialized
  }

  /** Toast message for preview mode feedback */
  readonly toastMessage = signal<string | null>(null);
  private toastTimeout: ReturnType<typeof setTimeout> | null = null;

  /** Show a toast message */
  private showToast(message: string): void {
    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
    }
    this.toastMessage.set(message);
    this.toastTimeout = setTimeout(() => {
      this.toastMessage.set(null);
    }, 3000);
  }

  /** Handle link click */
  onLinkClick(event: MouseEvent, link: { label: string; url: string }): void {
    const isLegal = this.isLegalUrl(link.url);
    const normalizedUrl = this.normalizeUrl(link.url);

    if (this.previewMode) {
      event.preventDefault(); // Always prevent default in preview mode

      if (isLegal) {
        // Emit event for legal page navigation
        const pageType = normalizedUrl.includes('imprint') ? 'imprint' : 'privacy';
        this.legalPageNavigation.emit({ pageType });
      }
    } else if (isLegal && normalizedUrl.startsWith('/')) {
      // In non-preview mode, handle internal legal links with programmatic navigation
      // This ensures SPA navigation works correctly on the dedicated calendar
      event.preventDefault();
      window.location.href = normalizedUrl;
    }
    // External links (not starting with /) will use the default href behavior
  }

  /** Computed layout variant with fallback for legacy property names */
  readonly layoutVariant = computed<FooterLayoutVariant>(() => {
    const settings = this._settings() as Record<string, unknown>;
    // Check all possible property names (camelCase and PascalCase variations)
    const variant = settings['layoutVariant']
      || settings['LayoutVariant']
      || settings['layout']
      || settings['Layout']
      || settings['variant']
      || settings['Variant'];

    // Map legacy values to current values
    if (variant === 'simple') return 'simple-centered';
    if (variant === 'columns' || variant === 'multi-column') return 'simple-centered';
    if (variant === 'centered') return 'simple-centered';

    return (variant as FooterLayoutVariant) || 'simple-centered';
  });

  /** Computed showSocialLinks with fallback for legacy property names */
  readonly showSocialLinks = computed(() => {
    const settings = this._settings() as Record<string, unknown>;
    // Check all possible property names
    const value = settings['showSocialLinks'] ?? settings['ShowSocialLinks'] ?? settings['showSocial'] ?? settings['ShowSocial'];
    return value !== false;
  });

  /** Computed social links (from settings, fallback to content for backward compatibility) */
  readonly socialLinks = computed(() => {
    const settings = this._settings();
    const content = this._content();
    // Check settings first, then content for legacy data
    return settings.socialLinks?.length ? settings.socialLinks : (content.socialLinks || []);
  });

  /** Computed background style (supports solid, gradient, inherit) */
  readonly backgroundStyle = computed(() => {
    const settings = this._settings();
    const bgType = settings.backgroundType;

    if (bgType === 'solid' && settings.backgroundColor) {
      return settings.backgroundColor;
    }
    if (bgType === 'gradient' && settings.backgroundGradient) {
      return settings.backgroundGradient;
    }
    // Default to theme surface color
    return this.themeSignal()?.surfaceColor || '#ffffff';
  });

  /** Helper to check if a value is a valid string (not empty, not "undefined") */
  private isValidString(value: unknown): value is string {
    return typeof value === 'string' && value.trim() !== '' && value !== 'undefined';
  }

  /** Computed company name (from settings, fallback to content for legacy) */
  readonly companyName = computed(() => {
    const settings = this._settings() as { companyName?: string };
    if (this.isValidString(settings.companyName)) return settings.companyName;
    const contentValue = this._content().companyName;
    return this.isValidString(contentValue) ? contentValue : '';
  });

  /** Computed tagline (from settings, fallback to content for legacy) */
  readonly tagline = computed(() => {
    const settings = this._settings() as { tagline?: string };
    if (this.isValidString(settings.tagline)) return settings.tagline;
    const contentValue = this._content().tagline;
    return this.isValidString(contentValue) ? contentValue : '';
  });

  /** Computed copyright year (from settings, fallback to current year) */
  readonly copyrightYear = computed(() => {
    const settings = this._settings() as { copyrightYear?: number | string };
    const year = settings.copyrightYear;
    // Handle both number and string (from form input)
    if (typeof year === 'number' && year > 0) return year;
    if (typeof year === 'string' && year !== 'undefined' && year.trim() !== '') {
      const parsed = parseInt(year, 10);
      if (!isNaN(parsed) && parsed > 0) return parsed;
    }
    return this.currentYear;
  });

  /** Computed copyright text - combines year and company name */
  readonly copyrightText = computed(() => {
    const settings = this._settings() as { copyrightText?: string };
    // Allow custom copyright text override
    if (this.isValidString(settings.copyrightText)) return settings.copyrightText;
    // Fallback to content copyrightText for legacy
    const contentCopyright = this._content().copyrightText;
    if (this.isValidString(contentCopyright)) return contentCopyright;
    // Generate default copyright text
    const company = this.companyName() || 'Your Company';
    return `© ${this.copyrightYear()} ${company}. All rights reserved.`;
  });

  /** Computed background color (deprecated - use backgroundStyle) */
  readonly backgroundColor = computed(() => {
    const settings = this._settings();
    if (settings.backgroundType === 'solid' && settings.backgroundColor) {
      return settings.backgroundColor;
    }
    return this.themeSignal()?.surfaceColor || '#ffffff';
  });

  /**
   * Computed signal that resolves navLinks to display format.
   * - Legal links: label/URL resolved from legalPageConfig based on locale
   * - Custom links: label resolved from labelDe/labelEn based on locale
   * - Legacy links: kept if no proper legal page config exists
   */
  readonly allNavLinks = computed<ResolvedNavLink[]>(() => {
    // Read signal values to establish dependencies
    // Use i18n.locale() for reactive updates when user clicks language button
    const locale = this.i18n.locale();
    const config = this._legalPageConfig();

    // First, check settings for navLinks (new format: shared across languages)
    const settingsLinks = (this._settings() as { navLinks?: NavLink[] }).navLinks || [];
    // Fallback to content navLinks for legacy data
    const contentLinks = this._content().navLinks || [];

    // Use settings navLinks if available, otherwise fall back to content
    const rawLinks = settingsLinks.length > 0 ? settingsLinks : contentLinks;

    // Check if we have proper legal page config
    const hasLegalConfig = config &&
      (config.imprintType !== 'None' || config.privacyType !== 'None');

    // Only filter out legacy legal links if we have a proper legal config
    // Otherwise, show the legacy links as-is
    const filteredLinks = hasLegalConfig
      ? rawLinks.filter(l => {
          if (l.type) return true; // Has explicit type field, keep it
          return !this.isLegacyLegalLink(l);
        })
      : rawLinks;

    // Check if we have legal items already (with proper type field)
    const hasLegalItems = filteredLinks.some(l => l.type === 'legal');

    // Resolve links to display format
    const resolvedLinks = filteredLinks.map(link => {
      if (link.type === 'legal') {
        return this.resolveLegalLinkInternal(link.legalKey, locale, config);
      } else {
        // Custom link - resolve label based on locale
        const label = locale === 'de'
          ? (link.labelDe || link.label || '')
          : (link.labelEn || link.label || '');
        return { label, url: link.url || '' };
      }
    }).filter((l): l is ResolvedNavLink => l !== null && l.label !== '');

    // If no legal items in the list and we have a legal config, add them from config
    if (!hasLegalItems && hasLegalConfig) {
      const legalLinks = this.legalLinks();
      return [...legalLinks, ...resolvedLinks];
    }

    return resolvedLinks;
  });

  /**
   * Resolve a legal link to display format using provided config (pure function for computed signal).
   */
  private resolveLegalLinkInternal(
    legalKey: 'imprint' | 'privacy' | undefined,
    locale: 'de' | 'en',
    config: LegalPageConfig | undefined
  ): ResolvedNavLink | null {
    if (!config || !legalKey) return null;

    if (legalKey === 'imprint') {
      if (config.imprintType === 'None' || !config.imprintType) return null;
      return {
        label: locale === 'de' ? 'Impressum' : 'Imprint',
        url: config.imprintType === 'External' && config.imprintExternalUrl
          ? config.imprintExternalUrl
          : '/imprint',
      };
    }

    if (legalKey === 'privacy') {
      if (config.privacyType === 'None' || !config.privacyType) return null;
      return {
        label: locale === 'de' ? 'Datenschutz' : 'Privacy Policy',
        url: config.privacyType === 'External' && config.privacyExternalUrl
          ? config.privacyExternalUrl
          : '/privacy',
      };
    }

    return null;
  }

  /**
   * Check if a link looks like a legacy legal page link (no type field but URL/label matches legal patterns).
   */
  private isLegacyLegalLink(link: { label?: string; url?: string; type?: string }): boolean {
    if (link.type) return false; // Has type field, not legacy

    const legalUrlPatterns = /^\/?(imprint|impressum|legal|legal-notice|datenschutz|privacy|terms|agb)$/i;
    const legalLabelPatterns = /imprint|impressum|legal.?notice|datenschutz|privacy|terms|agb/i;

    return legalUrlPatterns.test(link.url || '') || legalLabelPatterns.test(link.label || '');
  }

  /**
   * Normalize URL - convert absolute URLs pointing to the same domain to relative paths
   * This allows old absolute URLs (like https://subdomain.availme.net/imprint) to work
   * correctly as relative paths (/imprint) on the deployed site
   */
  normalizeUrl(url: string): string {
    if (!url) return url;

    // Already a relative URL
    if (url.startsWith('/') || url.startsWith('#')) {
      return url;
    }

    try {
      const parsed = new URL(url);
      const hostname = this.currentHostname || (typeof window !== 'undefined' ? window.location.hostname : '');

      // Check if the URL points to the same domain or an availme.net subdomain
      if (
        parsed.hostname === hostname ||
        (hostname && parsed.hostname.endsWith('.availme.net') && hostname.endsWith('.availme.net'))
      ) {
        // Return just the pathname (relative URL)
        return parsed.pathname + parsed.search + parsed.hash;
      }
    } catch {
      // Invalid URL, return as-is
    }

    return url;
  }

  /**
   * Check if a URL is a legal page URL (/privacy or /imprint).
   * Used to allow legal links to work even in preview mode.
   */
  isLegalUrl(url: string): boolean {
    if (!url) return false;
    const normalized = url.toLowerCase().replace(/^\/+/, '');
    return normalized === 'privacy' || normalized === 'imprint';
  }

  /** Computed legal links based on configuration (reactive via signals) */
  readonly legalLinks = computed<ResolvedNavLink[]>(() => {
    // Read signal values to establish dependencies
    // Use i18n.locale() for reactive updates when user clicks language button
    const config = this._legalPageConfig();
    const locale = this.i18n.locale();

    if (!config) return [];

    const links: ResolvedNavLink[] = [];

    // Imprint link
    if (config.imprintType === 'Internal') {
      links.push({
        label: locale === 'de' ? 'Impressum' : 'Imprint',
        url: '/imprint',
      });
    } else if (config.imprintType === 'External' && config.imprintExternalUrl) {
      links.push({
        label: locale === 'de' ? 'Impressum' : 'Imprint',
        url: config.imprintExternalUrl,
      });
    }

    // Privacy link
    if (config.privacyType === 'Internal') {
      links.push({
        label: locale === 'de' ? 'Datenschutz' : 'Privacy',
        url: '/privacy',
      });
    } else if (config.privacyType === 'External' && config.privacyExternalUrl) {
      links.push({
        label: locale === 'de' ? 'Datenschutz' : 'Privacy',
        url: config.privacyExternalUrl,
      });
    }

    return links;
  });

  /**
   * Pass-through for settings - normalization is now handled at source (page-editor/section-editor)
   * via the centralized normalizeKeys() utility.
   */
  private normalizeSettings(value: Record<string, unknown>): FooterSettings & { navLinks?: NavLink[] } {
    if (!value) return {};
    return value as FooterSettings & { navLinks?: NavLink[] };
  }

  /**
   * Pass-through for content - normalization is now handled at source (page-editor/section-editor)
   * via the centralized normalizeKeys() utility.
   */
  private normalizeContent(value: Record<string, unknown>): FooterContent {
    if (!value) return {};
    return value as FooterContent;
  }
}
